import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useDevice } from '../lib/device.js';
import {
  type AvatarConfig, type AvatarState, type AvatarPhoto,
  DEFAULT_AVATAR_CONFIG, DEFAULT_FACE_CONFIG, STYLE_PRESETS, mergeConfig, autoTuneFromPhoto, regionAt, loadAuthedImage, useAvatar,
} from '../lib/avatar.js';
import { detectFaceRegions } from '../lib/faceLandmarks.js';
import { usePrefs } from '../lib/prefs.js';
import { DotHead } from './DotHead.js';
import { VoiceControls } from './VoiceControls.js';

const STATES: AvatarState[] = ['idle', 'listening', 'thinking', 'speaking'];

// Tweak categories — one "gear" per group. Only one panel is shown at a time so
// the avatar preview always stays visible and there's never a scroll-in-scroll.
type Cat = 'form' | 'colors' | 'tint' | 'density' | 'face' | 'motion' | 'stage' | 'voice';
const CATS: { key: Cat; label: string }[] = [
  { key: 'form', label: 'Form' },
  { key: 'colors', label: 'Colors' },
  { key: 'tint', label: 'Tint' },
  { key: 'density', label: 'Density' },
  { key: 'face', label: 'Face' },
  { key: 'motion', label: 'Motion' },
  { key: 'stage', label: 'Stage' },
  { key: 'voice', label: 'Voice' },
];
function IconGear() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ── Small authed <img> (raw photo endpoint needs a Bearer token) ─────────────
// Owns its own object URL (revoked only on unmount / url change). We do NOT reuse
// loadAuthedImage here because that helper revokes its URL on the next tick — a
// DOM <img> pointing at it would race and intermittently render blank.
function AuthImg({ url, className }: { url: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let objUrl: string | null = null;
    let cancelled = false;
    const token = localStorage.getItem('mdai_token');
    fetch(`${import.meta.env.VITE_API_URL ?? ''}${url}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((blob) => { if (cancelled) return; objUrl = URL.createObjectURL(blob); setSrc(objUrl); })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [url]);
  return src
    ? <img src={src} className={className} alt="" />
    : <div className={`${className ?? ''} animate-pulse bg-white/[0.04]`} />;
}

// ── Controls ──────────────────────────────────────────────────────────────────
function Slider({ label, value, min, max, step = 0.01, onChange, fmt }: {
  label: string; value: number; min: number; max: number; step?: number;
  onChange: (v: number) => void; fmt?: (v: number) => string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-baseline">
        <span className="text-[11px] text-ink/55">{label}</span>
        <span className="font-mono text-[10px] text-ink/40 tabular-nums">{fmt ? fmt(value) : value.toFixed(2)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber h-1" />
    </div>
  );
}

function ColorDot({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 py-1 cursor-pointer">
      <span className="text-[11px] text-ink/55">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-ink/40">{value}</span>
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#ffc882'}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-5 rounded border border-ink/20 bg-transparent cursor-pointer p-0" />
      </span>
    </label>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-ink/[0.06] pt-3">
      <div className="text-[11px] text-ink/35 mb-2">{title}</div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </div>
  );
}

export function AvatarLab() {
  const qc = useQueryClient();
  const { config: savedConfig, photos, activePhoto, loading } = useAvatar();
  const [prefs, setPrefs] = usePrefs();

  const [draft, setDraft] = useState<AvatarConfig>(DEFAULT_AVATAR_CONFIG);
  const [previewMode, setPreviewMode] = useState<AvatarState>('idle');
  const [activeCat, setActiveCat] = useState<Cat>('form'); // one tweak group at a time.
  // A/B compare — stash a snapshot as "A" and flip the preview between A and the
  // live draft (B) to judge a change side by side.
  const [slotA, setSlotA] = useState<AvatarConfig | null>(null);
  const [showA, setShowA] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyUpload, setBusyUpload] = useState(false);
  // Delete-with-4-digit-code confirmation (prevents accidental removals).
  const [del, setDel] = useState<{ id: string; name: string; code: string } | null>(null);
  const [delInput, setDelInput] = useState('');
  function askDelete(id: string, name: string) {
    setDelInput('');
    setDel({ id, name, code: String(Math.floor(1000 + Math.random() * 9000)) });
  }
  const lastPhotoId = useRef<string | null | undefined>(undefined);
  // Each photo keeps its own config — reload the draft whenever the active photo
  // changes (and on first load).
  useEffect(() => {
    if (loading) return;
    const id = activePhoto?.id ?? null;
    if (lastPhotoId.current !== id) {
      lastPhotoId.current = id;
      // With no photo, the "None" default is the locked golden face — show ITS
      // actual config so the (read-only) sliders reflect what's really rendered.
      setDraft(activePhoto ? savedConfig : DEFAULT_FACE_CONFIG);
      setDirty(false);
    }
  }, [loading, activePhoto, savedConfig]);

  // ── Undo / redo ────────────────────────────────────────────────────────────
  // Rapid slider drags are coalesced into one history step (~450ms) so undo
  // moves in meaningful jumps rather than one-per-pixel.
  const pastRef = useRef<AvatarConfig[]>([]);
  const futureRef = useRef<AvatarConfig[]>([]);
  const lastSnap = useRef(0);
  const [, force] = useState(0);
  const rerender = () => force((x) => x + 1);

  function snapshot(prev: AvatarConfig) {
    const now = Date.now();
    if (now - lastSnap.current > 450) {
      pastRef.current.push(structuredClone(prev));
      if (pastRef.current.length > 60) pastRef.current.shift();
      futureRef.current = [];
      lastSnap.current = now;
      rerender();
    }
  }
  function undo() {
    const prev = pastRef.current.pop();
    if (!prev) return;
    futureRef.current.push(structuredClone(draft));
    setDraft(prev); setDirty(true); lastSnap.current = 0; rerender();
  }
  function redo() {
    const next = futureRef.current.pop();
    if (!next) return;
    pastRef.current.push(structuredClone(draft));
    setDraft(next); setDirty(true); lastSnap.current = 0; rerender();
  }

  function patch(p: Partial<AvatarConfig>) {
    snapshot(draft);
    setDraft((d) => mergeConfig({ ...d, ...p }));
    setDirty(true);
  }
  function patchColors(p: Partial<AvatarConfig['regionColors']>) {
    snapshot(draft);
    setDraft((d) => ({ ...d, regionColors: { ...d.regionColors, ...p } })); setDirty(true);
  }
  function patchDensity(p: Partial<AvatarConfig['regionDensity']>) {
    snapshot(draft);
    setDraft((d) => ({ ...d, regionDensity: { ...d.regionDensity, ...p } })); setDirty(true);
  }
  function patchTint(p: Partial<AvatarConfig['colorOpts']>) {
    snapshot(draft);
    setDraft((d) => ({ ...d, colorOpts: { ...d.colorOpts, ...p } })); setDirty(true);
  }
  function patchSpeed(p: Partial<AvatarConfig['stateSpeeds']>) {
    snapshot(draft);
    setDraft((d) => ({ ...d, stateSpeeds: { ...d.stateSpeeds, ...p } })); setDirty(true);
  }

  // ── Presets + import / export ────────────────────────────────────────────────
  const [toast, setToast] = useState<string | null>(null);
  function flash(msg: string) { setToast(msg); window.setTimeout(() => setToast(null), 1800); }

  function applyPreset(patchObj: Partial<AvatarConfig>) {
    snapshot(draft); lastSnap.current = 0; // force this to be its own undo step
    setDraft((d) => mergeConfig({
      ...d, ...patchObj,
      regionColors: { ...d.regionColors, ...(patchObj.regionColors ?? {}) },
      colorOpts: { ...d.colorOpts, ...(patchObj.colorOpts ?? {}) },
      regionDensity: { ...d.regionDensity, ...(patchObj.regionDensity ?? {}) },
    }));
    setDirty(true);
  }
  async function exportConfig() {
    const json = JSON.stringify(draft, null, 2);
    try { await navigator.clipboard.writeText(json); flash('Config copied to clipboard'); }
    catch { flash('Copy failed — see download'); }
    // Also offer a file download.
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'avatar-config.json';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  function importConfig() {
    const raw = window.prompt('Paste an avatar config (JSON):');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<AvatarConfig>;
      snapshot(draft); lastSnap.current = 0;
      setDraft(mergeConfig(parsed));
      setDirty(true);
      flash('Config imported');
    } catch { flash('Invalid JSON'); }
  }

  // Record a short clip of the live preview canvas and download it (webm/mp4 by
  // browser support) — share the avatar without a screen recorder.
  const previewBox = useRef<HTMLDivElement>(null);
  const [recording, setRecording] = useState(false);
  function recordClip() {
    const canvas = previewBox.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas || recording || typeof MediaRecorder === 'undefined') { flash('Recording unsupported'); return; }
    const stream = canvas.captureStream(30);
    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = mime === 'video/mp4' ? 'avatar.mp4' : 'avatar.webm';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setRecording(false);
      flash('Clip downloaded');
    };
    setRecording(true);
    rec.start();
    window.setTimeout(() => { try { rec.stop(); } catch { /* ignore */ } }, 4000);
  }

  // Auto-tune region colors from the active photo (dependency-free stand-in for
  // full face-landmark detection).
  const [tuning, setTuning] = useState(false);
  async function autoTune() {
    if (!activePhoto) return;
    setTuning(true);
    try {
      const img = await loadAuthedImage(activePhoto.url);
      const tuned = autoTuneFromPhoto(img, draft);
      applyPreset(tuned);
      flash('Colors tuned from photo');
    } catch { flash('Auto-tune failed'); }
    finally { setTuning(false); }
  }

  // Face-landmark detection (MediaPipe) → auto-place the region ellipses, then
  // auto-tune colors against the detected regions. Falls back gracefully.
  const [detecting, setDetecting] = useState(false);
  async function detectFace() {
    if (!activePhoto) return;
    setDetecting(true);
    flash('Detecting face…');
    try {
      const img = await loadAuthedImage(activePhoto.url);
      const regions = await detectFaceRegions(img);
      if (!regions) { flash('No face detected — using heuristics'); return; }
      snapshot(draft); lastSnap.current = 0;
      const withRegions = mergeConfig({ ...draft, faceRegions: regions });
      const tuned = autoTuneFromPhoto(img, withRegions);
      setDraft(mergeConfig({ ...withRegions, ...tuned,
        regionColors: { ...withRegions.regionColors, ...(tuned.regionColors ?? {}) } }));
      setDirty(true);
      flash('Face detected — regions placed & tuned');
    } catch { flash('Detection failed'); }
    finally { setDetecting(false); }
  }
  function clearRegions() {
    snapshot(draft); lastSnap.current = 0;
    setDraft((d) => { const { faceRegions: _drop, ...rest } = d; void _drop; return mergeConfig(rest); });
    setDirty(true);
    flash('Reverted to heuristic regions');
  }

  // ── Click-to-paint density ────────────────────────────────────────────────
  const [paint, setPaint] = useState(false);
  const [brushSign, setBrushSign] = useState<1 | -1>(1);
  const DENSITY_MAX: Record<string, number> = { eyes: 2, brows: 2, nose: 2, mouth: 2, skin: 1.5, hair: 1.5, beard: 1.5 };
  function paintAt(e: React.MouseEvent) {
    if (!paint || locked) return;
    const box = previewBox.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const S = previewSize;
    const px = (e.clientX - rect.left) * (S / rect.width);
    const py = (e.clientY - rect.top) * (S / rect.height);
    const aspect = (activePhoto?.width && activePhoto?.height) ? activePhoto.width / activePhoto.height : 0.82;
    const margin = 0.06;
    let boxW = S * (1 - margin * 2), boxH = boxW / aspect;
    if (boxH > S * (1 - margin * 2)) { boxH = S * (1 - margin * 2); boxW = boxH * aspect; }
    const boxX = (S - boxW) / 2, boxY = (S - boxH) / 2;
    const nx = (px - boxX) / boxW, ny = (py - boxY) / boxH;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
    const region = regionAt(nx, ny, draft.faceRegions);
    const max = DENSITY_MAX[region];
    if (max == null) { flash(`${region}: no density channel`); return; }
    const key = region as keyof AvatarConfig['regionDensity'];
    const next = Math.max(0, Math.min(max, draft.regionDensity[key] + brushSign * 0.15));
    patchDensity({ [key]: next } as Partial<AvatarConfig['regionDensity']>);
    flash(`${region} density → ${next.toFixed(2)}`);
  }

  // ── Static poster (PNG) — accessibility fallback / shareable still ─────────
  function savePoster() {
    const canvas = previewBox.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) { flash('No preview'); return; }
    try {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = 'avatar-poster.png';
      document.body.appendChild(a); a.click(); a.remove();
      flash('Poster saved');
    } catch { flash('Poster capture failed'); }
  }

  async function save() {
    if (!activePhoto) return; // None is the locked default — nothing to save
    setSaving(true);
    try {
      // Save the config onto the active photo so it keeps its complete tuning.
      await api(`/api/avatar/photos/${activePhoto.id}/config`, { method: 'PATCH', body: JSON.stringify({ config: draft }) });
      qc.invalidateQueries({ queryKey: ['avatar', 'photos'] });
      setDirty(false);
    } finally { setSaving(false); }
  }

  async function uploadPhotos(files: FileList) {
    setBusyUpload(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', file);
        const token = localStorage.getItem('mdai_token');
        await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/avatar/photos`, {
          method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: fd,
        });
      }
      qc.invalidateQueries({ queryKey: ['avatar', 'photos'] });
    } finally { setBusyUpload(false); }
  }
  async function activatePhoto(id: string) {
    await api(`/api/avatar/photos/${id}/activate`, { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['avatar', 'photos'] });
  }
  async function deactivateAll() {
    await api('/api/avatar/photos/deactivate', { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['avatar', 'photos'] });
  }
  async function deletePhoto(id: string) {
    await api(`/api/avatar/photos/${id}`, { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: ['avatar', 'photos'] });
  }
  async function confirmDelete() {
    if (!del || delInput.trim() !== del.code) return;
    await deletePhoto(del.id);
    setDel(null);
  }

  // The preview resamples when the build-affecting keys change; bump a key on
  // photo switch so it re-reads immediately.
  const resampleKey = useMemo(() => `${activePhoto?.id ?? 'none'}`, [activePhoto?.id]);

  // On mobile the layout flattens (display:contents) so the preview can stick to
  // the top while photos + tweaks scroll under it. Shrink the preview a touch so
  // it doesn't eat the screen while pinned.
  const { isMobile } = useDevice();
  const previewH = isMobile ? 220 : 300;
  const previewSize = isMobile ? 200 : 260;
  // The "None" default is a locked, non-editable version (the golden crystal face).
  const locked = !activePhoto;

  return (
    <>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-ink m-0">Avatar — particle lab</h3>
          <p className="font-serif italic text-[13px] text-ink/50 mt-0.5 max-w-prose">
            Tune the dot cloud live. Upload a portrait — the dots sample the active photo; Save persists it.
          </p>
        </div>
        {/* Render engine — a GLOBAL pref (works even on the locked default avatar).
            2D is the proven default; GPU (WebGL) draws far more glowing points. */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          <span className="text-[10px] text-ink/40">Render engine</span>
          <div className="inline-flex items-center gap-0.5 p-0.5 rounded-full border border-ink/12 bg-white/[0.02]">
            <button onClick={() => setPrefs({ avatarRenderer: '2d' })}
              className={`px-3 py-1 rounded-full text-[11px] transition-colors ${prefs.avatarRenderer === '2d' ? 'bg-amber text-void' : 'text-ink/50 hover:text-ink'}`}>2D</button>
            <button onClick={() => setPrefs({ avatarRenderer: 'webgl' })}
              className={`px-3 py-1 rounded-full text-[11px] transition-colors ${prefs.avatarRenderer === 'webgl' ? 'bg-amber text-void' : 'text-ink/50 hover:text-ink'}`}>GPU <span className="text-[7.5px] opacity-70">beta</span></button>
          </div>
        </div>
      </div>

      {/* Toolbar: style presets · undo/redo · import/export (disabled when locked) */}
      <div className={`mb-3 flex flex-wrap items-center gap-2 ${locked ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-ink/35 mr-0.5">Presets</span>
          {STYLE_PRESETS.map((pr) => (
            <button key={pr.name} onClick={() => applyPreset(pr.patch)}
              className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/60 hover:text-ink hover:border-ink/35">
              {pr.name}
            </button>
          ))}
        </div>
        <span className="w-px h-4 bg-ink/10 mx-0.5 hidden sm:block" />
        <button onClick={detectFace} disabled={!activePhoto || detecting} title="Detect the face (MediaPipe) and auto-place eyes/nose/mouth"
          className="px-2.5 py-1 rounded-full border border-sky-300/40 text-[11px] text-sky-200/80 hover:text-sky-100 hover:border-sky-300 disabled:opacity-30">
          {detecting ? 'Detecting…' : '🙂 Detect face'}
        </button>
        {draft.faceRegions && (
          <button onClick={clearRegions} title="Revert to the built-in heuristic regions"
            className="px-2 py-1 rounded-full border border-ink/15 text-[11px] text-ink/50 hover:text-ink hover:border-ink/35">clear</button>
        )}
        <button onClick={autoTune} disabled={!activePhoto || tuning} title="Auto-tune region colors from the active photo"
          className="px-2.5 py-1 rounded-full border border-amber/40 text-[11px] text-amber/80 hover:text-amber hover:border-amber disabled:opacity-30">
          {tuning ? 'Tuning…' : '✨ Auto-tune'}
        </button>
        <button onClick={() => setPaint((p) => !p)} title="Click-to-paint density: click a region on the preview to add/remove dots"
          className={`px-2.5 py-1 rounded-full border text-[11px] ${paint ? 'border-amber bg-amber/10 text-amber' : 'border-ink/15 text-ink/55 hover:text-ink hover:border-ink/35'}`}>
          🖌 Paint
        </button>
        {paint && (
          <button onClick={() => setBrushSign((s) => (s === 1 ? -1 : 1))} title="Brush direction: add or remove density"
            className="px-2.5 py-1 rounded-full border border-ink/15 font-mono text-[11px] leading-none text-ink/70 hover:text-ink hover:border-ink/35">
            {brushSign > 0 ? '＋' : '－'}
          </button>
        )}
        <span className="w-px h-4 bg-ink/10 mx-0.5 hidden sm:block" />
        <div className="flex items-center gap-1.5">
          <button onClick={undo} disabled={pastRef.current.length === 0} title="Undo"
            className="px-2.5 py-1 rounded-full border border-ink/15 font-mono text-[11px] leading-none text-ink/60 hover:text-ink hover:border-ink/35 disabled:opacity-30">↶</button>
          <button onClick={redo} disabled={futureRef.current.length === 0} title="Redo"
            className="px-2.5 py-1 rounded-full border border-ink/15 font-mono text-[11px] leading-none text-ink/60 hover:text-ink hover:border-ink/35 disabled:opacity-30">↷</button>
          <button onClick={importConfig} title="Import config JSON"
            className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/60 hover:text-ink hover:border-ink/35">Import</button>
          <button onClick={exportConfig} title="Export config JSON"
            className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/60 hover:text-ink hover:border-ink/35">Export</button>
        </div>
        <span className="w-px h-4 bg-ink/10 mx-0.5 hidden sm:block" />
        <div className="flex items-center gap-1.5">
          <button onClick={() => { setSlotA(structuredClone(draft)); setShowA(false); flash('Snapshot saved as A'); }}
            title="Save the current look as A to compare against"
            className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/60 hover:text-ink hover:border-ink/35">Set A</button>
          <button onClick={() => setShowA((v) => !v)} disabled={!slotA}
            title="Flip the preview between A (snapshot) and B (current)"
            className={`px-2.5 py-1 rounded-full border text-[11px] disabled:opacity-30 ${showA ? 'border-sky-400/50 text-sky-200' : 'border-ink/15 text-ink/60 hover:text-ink hover:border-ink/35'}`}>
            {showA ? 'Show B' : 'Show A'}
          </button>
          {slotA && (
            <button onClick={() => { snapshot(draft); lastSnap.current = 0; setDraft(slotA); setShowA(false); setDirty(true); flash('Loaded A'); }}
              title="Replace the current look with A"
              className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/60 hover:text-ink hover:border-ink/35">Load A</button>
          )}
        </div>
        {toast && <span className="text-[11px] text-amber/80">{toast}</span>}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[300px_1fr] md:gap-6 min-h-0">
        {/* Live preview — mobile: column flattens (contents), the preview box
            pins to the top. Desktop: the whole left column sticks so the avatar
            stays in view while the controls scroll on the right. */}
        <div className="contents md:flex md:flex-col md:gap-3 md:sticky md:top-2 md:self-start">
          <div ref={previewBox} onClick={paintAt}
            className={`relative rounded-2xl border border-ink/10 grid place-items-center overflow-hidden sticky top-0 z-20 bg-void md:static md:bg-transparent ${paint && !locked ? 'cursor-crosshair ring-1 ring-amber/40' : ''}`}
            style={{ height: previewH }}>
            <DotHead mode={previewMode} size={previewSize} config={showA && slotA ? slotA : draft}
              photoUrl={activePhoto?.url ?? null} resampleKey={resampleKey} />
            {slotA && (
              <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] ${showA ? 'bg-sky-400/80 text-void' : 'bg-amber/80 text-void'}`}>
                {showA ? 'A' : 'B'}
              </span>
            )}
          </div>
          <div className="flex gap-1.5 justify-center flex-wrap items-center">
            {STATES.map((s) => (
              <button key={s} onClick={() => setPreviewMode(s)}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${ previewMode === s ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/50 hover:text-ink' }`}>{s}</button>
            ))}
            <button onClick={recordClip} disabled={recording} title="Record a 4s clip of the avatar"
              className="px-2.5 py-1 rounded-full text-[11px] border border-rose-300/40 text-rose-200/80 hover:text-rose-100 hover:border-rose-300 disabled:opacity-40">
              {recording ? '● rec…' : '⦿ clip'}
            </button>
            <button onClick={savePoster} title="Save a static PNG poster of the avatar"
              className="px-2.5 py-1 rounded-full text-[11px] border border-ink/15 text-ink/55 hover:text-ink hover:border-ink/35">
              ▣ poster
            </button>
          </div>
          {paint && !locked && (
            <div className="text-center text-[10px] text-amber/70">
              click the face to {brushSign > 0 ? 'add' : 'remove'} density on that region
            </div>
          )}

          {/* Photos */}
          <Group title="Source photos">
            <div className="flex flex-wrap gap-2">
              {/* Always-first "None" → the LOCKED golden crystal-face default. */}
              <button onClick={deactivateAll} title="No photo — the locked golden crystal face"
                className={`w-16 h-16 rounded-lg border-2 grid place-items-center transition-colors ${!activePhoto ? 'border-amber bg-amber/[0.06]' : 'border-ink/15 hover:border-ink/40'}`}>
                <span className="text-[11px] text-center leading-tight text-ink/60">
                  none<br /><span className="text-amber/60">🔒 default</span>
                </span>
              </button>
              {photos.map((p: AvatarPhoto) => (
                <div key={p.id} onClick={() => { if (!p.isActive) void activatePhoto(p.id); }}
                  title={p.isActive ? 'Active avatar' : 'Click to set as the active avatar'}
                  className={`group relative w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${p.isActive ? 'border-amber' : 'border-ink/15 hover:border-ink/40'}`}>
                  <AuthImg url={p.url} className="w-full h-full object-cover" />
                  {p.isActive && (
                    <span className="absolute top-0.5 left-0.5 px-1 rounded-full bg-amber text-void text-[10px]">active</span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); askDelete(p.id, p.fileName); }} title="Delete photo"
                    className="absolute top-0.5 right-0.5 w-5 h-5 grid place-items-center rounded-full bg-black/70 text-[10px] leading-none text-ink/70 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity">🗑</button>
                </div>
              ))}
              <label className={`w-16 h-16 rounded-lg border-2 border-dashed border-ink/20 grid place-items-center cursor-pointer hover:border-ink/40 ${busyUpload ? 'opacity-50' : ''}`}>
                <span className="text-[11px] text-ink/40 text-center leading-tight">{busyUpload ? '…' : '+ add'}</span>
                <input type="file" accept="image/*" multiple className="hidden" disabled={busyUpload}
                  onChange={(e) => { if (e.target.files?.length) void uploadPhotos(e.target.files); e.target.value = ''; }} />
              </label>
            </div>
            {photos.length === 0 && (
              <p className="font-serif italic text-[12px] text-ink/40">No photo — the locked golden crystal face is shown. Add a portrait to make the dots resemble you.</p>
            )}
          </Group>
        </div>

        {/* Controls — a "gear" rail picks ONE category at a time, so the avatar
            preview stays visible and each panel is short (no scroll-in-scroll). */}
        <div className="contents md:flex md:flex-col md:gap-3 md:min-h-0">
          {locked && (
            <div className="px-3 py-2 rounded-lg border border-amber/30 bg-amber/[0.05] text-[11.5px] text-amber/85">
              🔒 Default avatar is locked — the golden crystal face. Select or add a photo to customize the dot cloud.
            </div>
          )}

          {/* Gear rail — wraps on desktop, swipes horizontally on mobile. */}
          <div className={`flex gap-1.5 ${isMobile ? 'overflow-x-auto -mx-5 px-5 pb-1 [scrollbar-width:none]' : 'flex-wrap'}`}>
            {CATS.map((c) => {
              const on = activeCat === c.key;
              const dim = locked && c.key !== 'voice'; // only Voice is editable while locked
              return (
                <button key={c.key} onClick={() => setActiveCat(c.key)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] transition-colors ${ on ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink hover:border-ink/35' } ${dim ? 'opacity-40' : ''}`}>
                  <IconGear />{c.label}
                </button>
              );
            })}
          </div>

          {/* Active category panel — only one group shows; it's short by design. */}
          <div className="rounded-xl border border-ink/[0.08] bg-white/[0.015] p-4">
            {activeCat === 'voice' ? (
              <VoiceControls />
            ) : (
              <div className={locked ? 'opacity-40 pointer-events-none select-none' : ''}>
                {activeCat === 'form' && (
                  <div className="flex flex-col gap-2.5">
                    <Slider label="Photo resemblance (real colors)" value={draft.photoColorMix} min={0} max={1} onChange={(v) => patch({ photoColorMix: v })} fmt={(v) => `${Math.round(v * 100)}%`} />
                    <Slider label="Fidelity (sharpness / focus)" value={draft.fidelity} min={0} max={1} onChange={(v) => patch({ fidelity: v })} />
                    <Slider label="Dot size" value={draft.dotSize} min={0.4} max={2} onChange={(v) => patch({ dotSize: v })} fmt={(v) => `${v.toFixed(2)}×`} />
                    <Slider label="Smile (−frown / +smile)" value={draft.smile} min={-1} max={1} onChange={(v) => patch({ smile: v })} fmt={(v) => v.toFixed(2)} />
                  </div>
                )}
                {activeCat === 'colors' && (
                  <div className="flex flex-col gap-2.5">
                    <ColorDot label="Skin" value={draft.regionColors.skin} onChange={(v) => patchColors({ skin: v })} />
                    <ColorDot label="Hair" value={draft.regionColors.hair} onChange={(v) => patchColors({ hair: v })} />
                    <ColorDot label="Beard" value={draft.regionColors.beard} onChange={(v) => patchColors({ beard: v })} />
                    <ColorDot label="Iris" value={draft.regionColors.iris} onChange={(v) => patchColors({ iris: v })} />
                    <ColorDot label="Lips" value={draft.regionColors.lips} onChange={(v) => patchColors({ lips: v })} />
                    <ColorDot label="Nose tint" value={draft.regionColors.noseTint} onChange={(v) => patchColors({ noseTint: v })} />
                    <ColorDot label="Brows" value={draft.regionColors.brows} onChange={(v) => patchColors({ brows: v })} />
                    <ColorDot label="Halo" value={/^#[0-9a-f]{6}$/i.test(draft.haloColor) ? draft.haloColor : '#ffc88c'}
                      onChange={(v) => patch({ haloColor: v })} />
                  </div>
                )}
                {activeCat === 'tint' && (
                  <div className="flex flex-col gap-2.5">
                    <Slider label="Iris" value={draft.colorOpts.iris} min={0} max={1.5} onChange={(v) => patchTint({ iris: v })} />
                    <Slider label="Lips" value={draft.colorOpts.lips} min={0} max={1.5} onChange={(v) => patchTint({ lips: v })} />
                    <Slider label="Nose" value={draft.colorOpts.nose} min={0} max={1.5} onChange={(v) => patchTint({ nose: v })} />
                    <Slider label="Brows" value={draft.colorOpts.brow} min={0} max={1.5} onChange={(v) => patchTint({ brow: v })} />
                  </div>
                )}
                {activeCat === 'density' && (
                  <div className="flex flex-col gap-2.5">
                    <Slider label="Skin" value={draft.regionDensity.skin} min={0} max={1.5} onChange={(v) => patchDensity({ skin: v })} />
                    <Slider label="Hair" value={draft.regionDensity.hair} min={0} max={1.5} onChange={(v) => patchDensity({ hair: v })} />
                    <Slider label="Beard" value={draft.regionDensity.beard} min={0} max={1.5} onChange={(v) => patchDensity({ beard: v })} />
                    <Slider label="Eyes" value={draft.regionDensity.eyes} min={0} max={2} onChange={(v) => patchDensity({ eyes: v })} />
                    <Slider label="Brows" value={draft.regionDensity.brows} min={0} max={2} onChange={(v) => patchDensity({ brows: v })} />
                    <Slider label="Nose" value={draft.regionDensity.nose} min={0} max={2} onChange={(v) => patchDensity({ nose: v })} />
                    <Slider label="Mouth" value={draft.regionDensity.mouth} min={0} max={2} onChange={(v) => patchDensity({ mouth: v })} />
                    <Slider label="Halo" value={draft.regionDensity.halo} min={0} max={2} onChange={(v) => patchDensity({ halo: v })} />
                  </div>
                )}
                {activeCat === 'face' && (
                  <div className="flex flex-col gap-2.5">
                    <Slider label="Eye openness" value={draft.eyeOpen} min={0} max={1} onChange={(v) => patch({ eyeOpen: v })} />
                    <Slider label="Teeth visibility" value={draft.teethVis} min={0} max={1.5} onChange={(v) => patch({ teethVis: v })} />
                  </div>
                )}
                {activeCat === 'motion' && (
                  <div className="flex flex-col gap-2.5">
                    <Slider label="Idle" value={draft.stateSpeeds.idle} min={0} max={2.5} onChange={(v) => patchSpeed({ idle: v })} />
                    <Slider label="Listening" value={draft.stateSpeeds.listening} min={0} max={2.5} onChange={(v) => patchSpeed({ listening: v })} />
                    <Slider label="Thinking" value={draft.stateSpeeds.thinking} min={0} max={2.5} onChange={(v) => patchSpeed({ thinking: v })} />
                    <Slider label="Speaking" value={draft.stateSpeeds.speaking} min={0} max={2.5} onChange={(v) => patchSpeed({ speaking: v })} />
                  </div>
                )}
                {activeCat === 'stage' && (
                  <div className="flex flex-col gap-2.5">
                    {/* Head isolation — keep only the head, drop the photo background.
                        Best paired with Detect face for an accurate head shape. */}
                    <label className="flex items-center justify-between gap-2 py-1 cursor-pointer">
                      <span className="text-[11px] text-ink/55">Isolate head (remove background)</span>
                      <input type="checkbox" checked={!!draft.cropToHead}
                        onChange={(e) => patch({ cropToHead: e.target.checked })}
                        className="w-4 h-4 accent-amber" />
                    </label>
                    <span className="font-mono text-[9px] text-ink/35 -mt-1">Keeps only the head; combine with “None” background below to fully drop the backdrop. Tip: run Detect face first for an accurate cut.</span>
                    <Slider label="Sampling resolution" value={draft.sampleW} min={120} max={600} step={10}
                      onChange={(v) => patch({ sampleW: Math.round(v) })} fmt={(v) => `${Math.round(v)}px`} />
                    <div className="flex flex-col gap-2 pt-1">
                      <span className="text-[11px] text-ink/55">Stage background</span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => patch({ stageBg: 'auto' })}
                          className={`px-3 py-1.5 rounded-full text-[11.5px] border ${draft.stageBg === 'auto' ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink'}`}>Auto</button>
                        <button onClick={() => patch({ stageBg: 'transparent' })}
                          className={`px-3 py-1.5 rounded-full text-[11.5px] border ${draft.stageBg === 'transparent' ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink'}`}>None</button>
                        <label className={`flex items-center gap-2 px-2 py-1 rounded-full border cursor-pointer ${draft.stageBg !== 'auto' && draft.stageBg !== 'transparent' ? 'border-amber' : 'border-ink/15'}`}>
                          <span className="text-[11.5px] text-ink/55">Custom</span>
                          <input type="color" value={/^#[0-9a-f]{6}$/i.test(draft.stageBg) ? draft.stageBg : '#0b0b0e'}
                            onChange={(e) => patch({ stageBg: e.target.value })}
                            className="w-6 h-5 rounded border border-ink/20 bg-transparent cursor-pointer p-0" />
                        </label>
                      </div>
                      <span className="font-mono text-[9px] text-ink/35">Auto = a backdrop that contrasts the current theme so the dots always read.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="mt-4 pt-3 border-t border-ink/10 flex items-center gap-3">
        <button onClick={save} disabled={locked || !dirty || saving}
          className="px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
          {saving ? 'Saving…' : dirty ? 'Save to database' : 'Saved'}
        </button>
        <button onClick={() => { setDraft(savedConfig); setDirty(false); }} disabled={locked}
          className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-30">
          Revert
        </button>
        <button onClick={() => { setDraft(structuredClone(DEFAULT_AVATAR_CONFIG)); setDirty(true); }} disabled={locked}
          className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-30">
          Reset defaults
        </button>
        {locked
          ? <span className="text-[11px] text-amber/70">🔒 default locked</span>
          : dirty && <span className="text-[11px] text-amber/70">unsaved changes</span>}
      </div>

      {/* Delete confirmation — type the 4-digit code to avoid accidental removals */}
      {del && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setDel(null)}>
          <div className="w-full max-w-xs rounded-2xl border border-ink/15 bg-void p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-[16px] text-ink">Delete “{del.name}”?</div>
            <p className="font-serif italic text-[13px] text-ink/55">This removes the photo and its tuning. To confirm, type the code below.</p>
            <div className="text-center font-mono text-2xl tracking-[0.4em] text-amber select-all py-1">{del.code}</div>
            <input value={delInput} onChange={(e) => setDelInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric" autoFocus placeholder="4-digit code"
              onKeyDown={(e) => { if (e.key === 'Enter') void confirmDelete(); }}
              className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-center text-lg tracking-[0.3em] text-ink focus:border-ink/40 focus:outline-none" />
            <div className="flex items-center gap-2 justify-end pt-1">
              <button onClick={() => setDel(null)} className="px-3 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">Cancel</button>
              <button onClick={() => void confirmDelete()} disabled={delInput !== del.code}
                className="px-4 py-1.5 rounded-full bg-red-400 text-void text-[11.5px] hover:bg-red-300 disabled:opacity-30">Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
